import { BrowserRouter, Route, Routes } from 'react-router-dom';
import LoginScreen from './auth/LoginScreen';
import { signOut, useSession } from './auth/useSession';
import { useAdminGate } from './admin/useAdminGate';
import { Layout } from './components/Layout';
import DashboardPage from './pages/DashboardPage';
import AlertsPage from './pages/AlertsPage';
import AnalystPage from './pages/AnalystPage';
import MissionControlPage from './pages/MissionControlPage';
import OperationsPage from './pages/OperationsPage';
import { COLORS } from './theme';

export default function App() {
  return (
    <BrowserRouter>
      <SessionRoutes />
    </BrowserRouter>
  );
}

function SessionRoutes() {
  const { session, loading } = useSession();

  if (loading) return <SplashState label="Warming up…" />;
  if (!session) return <LoginScreen />;

  return <AdminGate email={session.user.email ?? null} userId={session.user.id} />;
}

function AdminGate({ email, userId }: { email: string | null; userId: string }) {
  const gate = useAdminGate(userId);

  if (gate.status === 'loading') return <SplashState label="Verifying admin access…" />;
  if (gate.status === 'not_admin') return <NotAuthorized email={email} />;
  if (gate.status === 'error')     return <NotAuthorized email={email} error={gate.message} />;

  return (
    <Routes>
      <Route element={<Layout email={email} />}>
        <Route index           element={<MissionControlPage />} />
        <Route path="dashboard"  element={<DashboardPage />} />
        <Route path="operations" element={<OperationsPage />} />
        <Route path="alerts"    element={<AlertsPage />} />
        <Route path="analyst"   element={<AnalystPage />} />
        <Route path="*"         element={<MissionControlPage />} />
      </Route>
    </Routes>
  );
}

function SplashState({ label }: { label: string }) {
  return (
    <div style={splashStyle}>
      <div style={{ color: COLORS.textMuted, fontSize: 13, letterSpacing: 0.4 }}>{label}</div>
    </div>
  );
}

function NotAuthorized({ email, error }: { email: string | null; error?: string }) {
  return (
    <div style={splashStyle}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <div style={{ color: COLORS.warn, fontSize: 24, marginBottom: 12 }}>Not authorized</div>
        <div style={{ color: COLORS.textSecondary, fontSize: 14, lineHeight: 1.6 }}>
          {email ? <>Signed in as <b>{email}</b>.</> : null} This account is not on the admin allowlist.
        </div>
        {error && (
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 16, fontStyle: 'italic' }}>
            {error}
          </div>
        )}
        <button
          onClick={() => { void signOut(); }}
          style={{
            marginTop: 24, background: 'transparent', color: COLORS.textSecondary,
            border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 20px',
            fontSize: 13, cursor: 'pointer',
          }}
        >Sign out</button>
      </div>
    </div>
  );
}

const splashStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', background: COLORS.bg,
};
